import { useQueryClient } from '@tanstack/react-query';
import { GetStaticProps } from 'next';
import Link from 'next/link';
import { useTranslation } from 'next-i18next';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import { FC, useEffect } from 'react';

import GeneralMessage from '@/components/GeneralMessage';
import PageTitle from '@/components/PageTitle';
import Spinner from '@/components/Spinner';
import TwoColumnsLayout from '@/components/TwoColumnsLayout';
import useGetApiHealth from '@/hooks/api/health/useGetApiHealth';
import useGetIdpHealth from '@/hooks/api/health/useGetIdpHealth';
import {
  ACTIVITY_CART,
  INSTITUTION_COLOR,
  STATUS_YLAH_URL,
} from '@/utils/constans';

import Button from '../../components/Button';
import styles from './Welcome.module.scss';

const WelcomePage: FC = () => {
  const { t } = useTranslation(['common', 'welcome']);
  const queryClient = useQueryClient();

  const { isApiAvailable, isPending: isApiHealthLoading } = useGetApiHealth();
  const { isIdpAvailable, isPending: isIdpHealthLoading } = useGetIdpHealth();
  const isPending = isApiHealthLoading || isIdpHealthLoading;
  const hasProblem = !isApiAvailable || !isIdpAvailable;

  useEffect(() => {
    localStorage.removeItem(ACTIVITY_CART);
    localStorage.removeItem(INSTITUTION_COLOR);
    queryClient.clear();
  }, [queryClient]);

  return (
    <TwoColumnsLayout isAuthenticatedPath={false}>
      <div className={styles.container}>
        {isPending ? (
          <Spinner />
        ) : (
          <>
            <PageTitle title={t('welcome:welcome-header')} />
            <h1 className={styles.header}>{t('welcome:welcome-header')}</h1>
            <p className={styles.description}>
              {t('welcome:welcome-description')}
            </p>
            {hasProblem && (
              <GeneralMessage
                header={t('common:system-not-available-modal.header')}
                type="error"
                className={styles.systemProblemMessage}
                role="alert"
                aria-live="assertive"
              >
                <p className={styles.message}>
                  {t('common:system-not-available-modal.message')}{' '}
                  {t('common:system-not-available-modal.try-logging-in-later')}{' '}
                  <Link
                    href={STATUS_YLAH_URL}
                    target="_blank"
                    rel="noreferrer"
                    className={styles.link}
                  >
                    {t('common:system-not-available-modal.url-label')}
                  </Link>
                </p>
              </GeneralMessage>
            )}
            <form className={styles.form} action={'/api/auth/sign-in'}>
              <Button
                type="submit"
                text={t('common:log-in')}
                disabled={hasProblem}
              />
            </form>
          </>
        )}
      </div>
    </TwoColumnsLayout>
  );
};

export const getStaticProps: GetStaticProps = async ({ locale }) => {
  return {
    props: {
      ...(await serverSideTranslations(locale ?? '', [
        'common',
        'welcome',
        'errors',
        'notifications',
      ])),
    },
  };
};

export default WelcomePage;
